import * as O from 'fp-ts/lib/Option';
import { ErrorMessagesNumberRangeFilter } from '~/utils/errors/errorMessages';
import { Filter } from './Filter';
import {
    Filter_Config,
    Filter_ControlsState,
    Filter_Snapshot,
    Filter_State,
    FilterAggregation,
} from './types/filters.type';
import { FilterType } from './types/filterType.type';
import {
    NumberFilter_Config,
    NumberFilter_ControlsState,
    NumberFilter_RequestParams,
    NumberFilter_Snapshot,
    NumberFilter_State,
    NumberFilterAggregation,
    UpdateNumberFilterPayload_ControlsState,
} from './types/numberFilter.type';

export class NumberFilter implements Filter {
    private type: FilterType;

    constructor() {
        this.type = 'number';
    }

    public isNumberFilterConfig(config: Filter_Config): config is NumberFilter_Config {
        return config.type === 'float' || config.type === 'integer';
    }

    public isNumberFilterState(state: Filter_State): state is NumberFilter_State {
        return state.type === 'float' || state.type === 'integer';
    }

    public isNumberFilterControlsState(
        controlsState: Filter_ControlsState
    ): controlsState is NumberFilter_ControlsState {
        return controlsState.type === 'float' || controlsState.type === 'integer';
    }

    public isNumberFilterSnapshot(snapshot: Filter_Snapshot): snapshot is NumberFilter_Snapshot {
        return (
            typeof snapshot === 'object' &&
            snapshot !== null &&
            'min' in snapshot &&
            'max' in snapshot &&
            'identifier' in snapshot
        );
    }

    public resetControlsState(controlsState: Filter_ControlsState): Filter_ControlsState {
        return controlsState;
    }

    public isNumberFilterAggregation(aggregation: FilterAggregation): aggregation is NumberFilterAggregation {
        return 'min' in aggregation && 'max' in aggregation;
    }

    public createStateFromConfig(config: NumberFilter_Config): NumberFilter_State {
        if (config.values.min.value > config.values.max.value) {
            throw new Error(ErrorMessagesNumberRangeFilter.INTEGER_FILTER_MIN_GREATER_MAX);
        }
        config.values.min.displayValue = config.values.min.value;
        config.values.max.displayValue = config.values.max.value;
        config.disabled = false;
        return { ...config };
    }

    public createControlsStateFromConfig(config: NumberFilter_Config): NumberFilter_ControlsState {
        return {
            identifier: config.identifier,
            type: config.type,
            applyFilter: false,
            values: {
                min: config.values.min.value,
                max: config.values.max.value,
            },
        };
    }

    public updateControlsState(
        controlsState: NumberFilter_ControlsState,
        state: NumberFilter_State,
        payload: UpdateNumberFilterPayload_ControlsState
    ): NumberFilter_ControlsState {
        return {
            ...controlsState,
            applyFilter: payload.value.min !== state.values.min.value || payload.value.max !== state.values.max.value,
            values: {
                ...controlsState.values,
                min: payload.value.min,
                max: payload.value.max,
            },
        };
    }

    public applyAggregation(
        state: NumberFilter_State,
        controlsState: NumberFilter_ControlsState,
        aggregation: NumberFilterAggregation
    ): NumberFilter_State {
        if (aggregation.min === null || aggregation.max === null) {
            return { ...state, disabled: true };
        }

        let displayMin = controlsState.values.min < aggregation.min ? aggregation.min : controlsState.values.min;
        let displayMax = controlsState.values.max > aggregation.max ? aggregation.max : controlsState.values.max;

        if (controlsState.values.min > aggregation.max) {
            displayMin = aggregation.min;
        }

        if (controlsState.values.max < aggregation.min) {
            displayMax = aggregation.max;
        }

        return {
            ...state,
            values: {
                histogram: aggregation.histogram,
                min: {
                    value: aggregation.min,
                    displayValue: displayMin,
                },
                max: {
                    value: aggregation.max,
                    displayValue: displayMax,
                },
            },
            disabled: false,
        };
    }

    public cancelFilter(
        controlsState: NumberFilter_ControlsState,
        payload: null,
        config: NumberFilter_Config
    ): NumberFilter_ControlsState {
        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                min: config.values.min.value,
                max: config.values.max.value,
            },
            applyFilter: false,
        };
    }

    public createRequestPayloadFromState(state: NumberFilter_ControlsState): O.Option<NumberFilter_RequestParams> {
        if (state.applyFilter) {
            return O.some({
                name: state.identifier,
                from: state.values.min,
                to: state.values.max,
            });
        }
        return O.none;
    }

    public createSnapshotFromState(controlsState: NumberFilter_ControlsState): O.Option<NumberFilter_Snapshot> {
        if (controlsState.applyFilter) {
            return O.some({
                identifier: controlsState.identifier,
                min: controlsState.values.min,
                max: controlsState.values.max,
            });
        }
        return O.none;
    }

    public applySnapshotToControlsState(controlsState: NumberFilter_ControlsState, snapshot: NumberFilter_Snapshot) {
        return {
            ...controlsState,
            values: {
                ...controlsState.values,
                min: snapshot.min,
                max: snapshot.max,
            },
            applyFilter: true,
        };
    }
}
